import {
  usePatchAssetUploadCompleteApi,
  usePostAssetUploadPresignedApi,
  usePutUploadFileToS3,
  usePutUploadThumbnailToS3,
  AssetUploadCreateInfoDto,
} from '@/shared/apis/asset-upload';
import { ProjectUploadInfo } from '@/shared/apis/asset-upload/post-presigned-url';
import type { UploadPreflightTarget } from '@/shared/apis/asset-upload/post-upload-preflight';
import {
  initiateMultipartUploadApi,
  completeMultipartUploadApi,
  abortMultipartUploadApi,
} from '@/shared/apis/asset-upload/multipart-upload-api';
import { uploadMultipartToS3 } from '@/shared/apis/asset-upload/upload-multipart-to-s3';
import { MULTIPART_THRESHOLD } from '@/shared/apis/asset-upload/smart-upload';
import {
  extractThumbnailFromVideo,
  getResolutionFromDimensions,
  getVideoMetadata,
  getAudioMetadata,
  getImageMetadata,
  getFontMetadata,
} from '@/shared';
import { applyFastStart } from '@/shared/utils/mp4-faststart';
import { warnIfUnsupportedAudioCodec } from '@/shared/utils/audio-codec-check';
import { DtoExtension, DtoProviderType } from '@/shared/constants';
import { AxiosError } from 'axios';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import { getFileTypeFromBytes } from '@/shared/utils/file-type-check';
import { resolveBackendDtoExtension } from '@/shared/utils/backend-file-extension';
import { toast } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { reportClassifiedUploadFailure } from '@/shared/utils/report-upload-failure';
import {
  classifyUploadPreflightFailure,
  runUploadPreflightIfNeeded,
} from '@/shared/utils/upload-preflight';
import { showUploadFailureToast } from '@/shared/utils/upload-failure-toast';

// 공용 타입·에러는 React 의존성 없는 별도 파일로 추출 (테스트 chain 폭발 회피용).
// 후방 호환을 위해 re-export 유지.
export type { OnCompleteResult } from './upload-types';
export { UploadError } from './upload-types';
import type { OnCompleteResult } from './upload-types';
import { UploadError } from './upload-types';

// 공통 업로드 파라미터 타입 (v2.0)
interface BaseUploadParams {
  spaceId?: number;
  folderId?: number;
  file: File;
  project?: ProjectUploadInfo;
  onProgress?: (fileId: string, progress: number) => void;
  onComplete?: (result: OnCompleteResult) => void;
}

// 공통 업로드 결과 타입
interface UploadResult {
  success: boolean;
  message: string;
}

// 메타데이터 기본 타입
interface BaseMetadata {
  fileName: string;
  fileExtension: string;
  fileSize: number;
}

interface UploadPresignedData {
  id: number;
  preSignedUrl: string;
  thumbnailPreSignedUrl?: string;
}

/**
 * 업로드 대상 버킷. preflight 와 실패 진단 probe 가 같은 버킷을 봐야 한다.
 */
const resolveUploadTarget = (
  project?: ProjectUploadInfo,
): UploadPreflightTarget =>
  project && !project.isAsset ? 'PROJECTS' : 'V_ITEMS';

const resolveDtoExtension = async (
  file: File,
  fallbackExtension: string,
): Promise<DtoExtension> => {
  const detectedFileType = await getFileTypeFromBytes(file);
  return resolveBackendDtoExtension({
    fileName: file.name,
    detectedExtension: detectedFileType?.ext,
    fallbackExtension,
  });
};

/**
 * Asset Upload Hook
 *
 * v2.0: 프로젝트와 무관하게 MySpace에 에셋을 업로드하는 hook
 */
export const useAssetUpload = () => {
  const { mutateAsync: getPresignedUrl } = usePostAssetUploadPresignedApi();
  const { mutateAsync: uploadToS3 } = usePutUploadFileToS3();
  const { mutateAsync: uploadThumbnailToS3 } = usePutUploadThumbnailToS3();
  const { mutateAsync: complete } = usePatchAssetUploadCompleteApi();
  const t = useTranslations();

  const requestPresignedUrl = async ({
    spaceId,
    fileExtension,
    fileName,
    fileSize,
    content,
    project,
  }: {
    spaceId?: number;
    fileExtension: DtoExtension;
    fileName: string;
    fileSize: number;
    content: AssetUploadCreateInfoDto;
    project?: ProjectUploadInfo;
  }) => {
    try {
      // v2.0: folderId는 uploadComplete 시 전달
      // ProjectContentInputDto.LocalFile과 동일한 스키마 사용
      const response = await getPresignedUrl({
        type: 'LOCAL_FILE',
        name: fileName,
        extension: fileExtension,
        size: fileSize,
        content: content,
        spaceId: spaceId,
        providerType: DtoProviderType['local'],
        project,
      });

      const responseData = response.data;

      return {
        id: responseData.id,
        preSignedUrl: responseData.preSignedUrl,
        thumbnailPreSignedUrl: responseData.thumbnailPreSignedUrl || undefined,
      };
    } catch (e) {
      if (e instanceof AxiosError) {
        const status = e.response?.status;
        const serverCode = e.response?.data?.code;
        console.error('[Upload] Presigned URL 요청 실패:', {
          status,
          code: serverCode,
          data: e.response?.data,
          fileName,
          fileSize,
        });
        throw new Error(
          `Presigned URL 실패 HTTP ${status ?? 'unknown'} (code: ${serverCode ?? '-'}, 파일: ${fileName}, 크기: ${(fileSize / 1024 / 1024).toFixed(1)}MB)`,
        );
      } else {
        const msg = e instanceof Error ? e.message : String(e);
        console.error('[Upload] Presigned URL 요청 실패 (unknown):', e);
        throw new Error(
          `Presigned URL 실패: ${msg} (파일: ${fileName}, 크기: ${(fileSize / 1024 / 1024).toFixed(1)}MB)`,
        );
      }
    }
  };

  const runAssetPreflight = async ({
    file,
    project,
    spaceId,
  }: {
    file: File;
    project?: ProjectUploadInfo;
    spaceId?: number;
  }) => {
    const target = resolveUploadTarget(project);
    try {
      await runUploadPreflightIfNeeded({
        file,
        target,
        uploadPath: 'shared/useAssetUpload',
        browserDirect: true,
      });
    } catch (error) {
      const classification = classifyUploadPreflightFailure(error, file.name);
      showUploadFailureToast(t, classification);
      reportClassifiedUploadFailure(error, file, classification, {
        spaceId,
        uploadPath: 'shared/useAssetUpload/preflight',
      });
      throw error;
    }
  };

  /**
   * 공통 업로드 로직을 처리합니다.
   * S3 업로드 → 완료 처리 → 콜백 실행
   */
  const executeUpload = async ({
    file,
    uploadData,
    fileId,
    folderId,
    project,
    onProgress,
    onComplete,
  }: {
    file: File;
    uploadData: UploadPresignedData;
    fileId: string;
    folderId?: number;
    project?: ProjectUploadInfo;
    onProgress?: (fileId: string, progress: number) => void;
    onComplete?: (result: OnCompleteResult) => void;
  }): Promise<UploadResult> => {
    await uploadToS3({
      file: file,
      presignedUrl: uploadData.preSignedUrl,
      onProgress: (progress) => {
        onProgress?.(fileId, progress);
      },
      diagnosticsTarget: resolveUploadTarget(project),
    });

    await complete({ contentId: uploadData.id, folderId });

    onComplete?.({
      id: uploadData.id,
      preSignedUrl: uploadData.preSignedUrl,
      thumbnailPreSignedUrl: uploadData.thumbnailPreSignedUrl,
    });

    return { success: true, message: 'Upload completed successfully.' };
  };

  /**
   * 멀티파트 업로드를 실행합니다.
   * initiate → 청크 병렬 업로드 → complete (실패 시 abort)
   */
  const executeMultipartUpload = async ({
    file,
    presignedRequest,
    fileId,
    folderId,
    onProgress,
    onComplete,
    beforeComplete,
  }: {
    file: File;
    presignedRequest: Parameters<typeof initiateMultipartUploadApi>[0];
    fileId: string;
    folderId?: number;
    onProgress?: (fileId: string, progress: number) => void;
    onComplete?: (result: OnCompleteResult) => void;
    /** 청크 업로드 완료 후, complete API 호출 전에 실행할 콜백 (예: 썸네일 업로드) */
    beforeComplete?: (thumbnailPreSignedUrl: string | null) => Promise<void>;
  }): Promise<UploadResult> => {
    const initResponse = await initiateMultipartUploadApi(presignedRequest);
    const {
      id: contentId,
      uploadId,
      partSize,
      partPresignedUrls,
      thumbnailPreSignedUrl,
    } = initResponse.data;

    try {
      // MP4/MOV fast-start 변환 (Streaming 방식, 파일 크기 제한 없음)
      let uploadFile = file;
      try {
        uploadFile = await applyFastStart(file);
      } catch (e) {
        console.warn(
          '[mp4-faststart] fast-start 처리 실패, 원본 파일로 업로드:',
          e,
        );
      }

      const completedParts = await uploadMultipartToS3({
        file: uploadFile,
        partPresignedUrls,
        partSize,
        onProgress: (progress) => {
          onProgress?.(fileId, progress);
        },
      });

      // 청크 업로드 완료 후, complete 전에 추가 작업 (예: 썸네일)
      await beforeComplete?.(thumbnailPreSignedUrl);

      await completeMultipartUploadApi({
        contentId,
        uploadId,
        parts: completedParts,
        folderId,
      });

      onProgress?.(fileId, 100);
      onComplete?.({
        id: contentId,
        preSignedUrl: '',
        thumbnailPreSignedUrl: thumbnailPreSignedUrl ?? undefined,
      });

      return {
        success: true,
        message: 'Multipart upload completed successfully.',
      };
    } catch (error) {
      await abortMultipartUploadApi({ contentId, uploadId });
      throw new UploadError(
        error instanceof Error ? error.message : String(error),
        contentId,
      );
    }
  };

  /**
   * 기본 업로드 워크플로우를 실행합니다.
   * 메타데이터 추출 → 검증 → presigned URL 요청 → 업로드 → 완료
   *
   * 파일 크기가 MULTIPART_THRESHOLD(50MB) 이상이면 멀티파트 업로드를 사용합니다.
   */
  const performBaseUpload = async <T extends BaseMetadata>({
    file,
    spaceId,
    folderId,
    fileId,
    project,
    getMetadata,
    createContent,
    onProgress,
    onComplete,
    contentType,
  }: {
    file: File;
    spaceId?: number;
    folderId?: number;
    fileId: string;
    project?: ProjectUploadInfo;
    getMetadata: (file: File) => Promise<T>;
    createContent: (metadata: T) => AssetUploadCreateInfoDto;
    onProgress?: (fileId: string, progress: number) => void;
    onComplete?: (result: OnCompleteResult) => void;
    contentType: string;
  }): Promise<UploadResult | undefined> => {
    // 1. 메타데이터 추출
    const metadata = await getMetadata(file);
    if (!metadata) {
      throw new Error(`${contentType} metadata is not available.`);
    }

    await runAssetPreflight({ file, project, spaceId });

    // 2. 파일 크기에 따라 업로드 방식 분기
    const dtoExtension = await resolveDtoExtension(
      file,
      metadata.fileExtension,
    );
    const content = createContent(metadata);

    if (file.size >= MULTIPART_THRESHOLD) {
      // 대용량 파일: 멀티파트 업로드
      const presignedRequest = {
        type: 'LOCAL_FILE' as const,
        name: metadata.fileName,
        extension: dtoExtension,
        size: metadata.fileSize,
        content,
        spaceId,
        providerType: DtoProviderType['local'],
        project,
      };

      return await executeMultipartUpload({
        file,
        presignedRequest,
        fileId,
        folderId,
        onProgress,
        onComplete,
      });
    }

    // 소용량 파일: 기존 단일 PUT 업로드
    const uploadData = await requestPresignedUrl({
      spaceId,
      fileExtension: dtoExtension,
      fileName: metadata.fileName,
      fileSize: metadata.fileSize,
      content,
      project,
    });

    if (!uploadData) {
      throw new Error('Upload data is not available.');
    }

    // 3. S3 업로드 및 완료 처리
    try {
      return await executeUpload({
        file,
        uploadData,
        fileId,
        folderId,
        project,
        onProgress,
        onComplete,
      });
    } catch (e) {
      throw new UploadError(
        e instanceof Error ? e.message : String(e),
        uploadData.id,
      );
    }
  };

  const videoUpload = async ({
    spaceId,
    folderId,
    file,
    project,
    onProgress,
    onComplete,
  }: BaseUploadParams) => {
    const fileId = generateRandomId();

    // 1. 메타데이터 추출
    const metadata = await getVideoMetadata(file);
    if (!metadata) {
      throw new Error('Video metadata is not available.');
    }

    // 1-1. 오디오 코덱 사전 체크: 업로드 시작 전에 토스트가 먼저 보이도록 await.
    // 5MB 헤더만 슬라이스하므로 추가 지연은 일반적으로 100ms 이내(추론).
    await warnIfUnsupportedAudioCodec(file, () => {
      toast({
        description: t('common.audio-transcode-warning'),
        toastOptions: { position: 'top-center' },
      });
    });

    await runAssetPreflight({ file, project, spaceId });

    // 2. 파일 크기에 따라 업로드 방식 분기
    const dtoExtension = await resolveDtoExtension(
      file,
      metadata.fileExtension,
    );
    const content = {
      type: 'VIDEO' as const,
      duration: metadata.duration,
      height: metadata.height,
      width: metadata.width,
      resolution: getResolutionFromDimensions(metadata.width, metadata.height),
    };

    if (file.size >= MULTIPART_THRESHOLD) {
      // 대용량 비디오: 멀티파트 업로드
      let multipartThumbnailUploaded = false;
      return await executeMultipartUpload({
        file,
        presignedRequest: {
          type: 'LOCAL_FILE' as const,
          name: metadata.fileName,
          extension: dtoExtension,
          size: metadata.fileSize,
          content,
          spaceId,
          providerType: DtoProviderType['local'],
          project,
        },
        fileId,
        folderId,
        onProgress,
        onComplete: (result) => {
          onComplete?.({
            ...result,
            thumbnailUploaded: multipartThumbnailUploaded,
          });
        },
        beforeComplete: async (thumbnailPreSignedUrl) => {
          // 비디오 썸네일 추출 및 업로드 (best-effort)
          // 실패 시 서버(AI media-process) 폴백 생성에 위임하여 전체 업로드 abort 방지.
          if (thumbnailPreSignedUrl) {
            try {
              const thumbnailBlob = await extractThumbnailFromVideo(
                file,
                metadata.duration,
              );
              if (thumbnailBlob) {
                await uploadThumbnailToS3({
                  thumbnailBlob,
                  presignedUrl: thumbnailPreSignedUrl,
                });
                multipartThumbnailUploaded = true;
              } else {
                console.warn(
                  '[Upload] 멀티파트 썸네일 추출 실패 (서버 폴백 생성으로 위임)',
                );
              }
            } catch (thumbErr) {
              console.warn(
                '[Upload] 멀티파트 썸네일 처리 실패 (서버 폴백 생성으로 위임)',
                thumbErr,
              );
            }
          } else {
            // 썸네일 presigned URL 자체가 없는 케이스는 서버가 생성할 수 없음 → true로 간주
            multipartThumbnailUploaded = true;
          }
        },
      });
    }

    // 소용량 비디오: 기존 단일 PUT 업로드
    const uploadData = await requestPresignedUrl({
      spaceId,
      fileExtension: dtoExtension,
      fileName: metadata.fileName,
      fileSize: metadata.fileSize,
      content,
      project,
    });

    if (!uploadData) {
      throw new Error('Upload data is not available.');
    }

    // 3. S3 업로드 (비디오 파일)
    try {
      await uploadToS3({
        file,
        presignedUrl: uploadData.preSignedUrl,
        onProgress: (progress) => {
          onProgress?.(fileId, progress);
        },
        diagnosticsTarget: resolveUploadTarget(project),
      });

      // 4. 썸네일 추출 및 업로드 (best-effort)
      // HEVC 등 브라우저 디코딩 실패 시 썸네일 생성이 null로 반환되거나 업로드가 실패할 수 있음.
      // 이 경우 업로드 전체를 중단하지 않고 서버(AI media-process) 폴백 생성에 위임한다.
      let thumbnailUploaded = false;
      if (uploadData.thumbnailPreSignedUrl) {
        try {
          const thumbnailBlob = await extractThumbnailFromVideo(
            file,
            metadata.duration,
          );
          if (thumbnailBlob) {
            await uploadThumbnailToS3({
              thumbnailBlob,
              presignedUrl: uploadData.thumbnailPreSignedUrl,
            });
            thumbnailUploaded = true;
          } else {
            console.warn('[Upload] 썸네일 추출 실패 (서버 폴백 생성으로 위임)');
          }
        } catch (thumbErr) {
          console.warn(
            '[Upload] 썸네일 처리 실패 (서버 폴백 생성으로 위임)',
            thumbErr,
          );
        }
      } else {
        // 썸네일 presigned URL 자체가 없으면 서버가 생성할 수 없는 케이스이므로 true로 간주
        thumbnailUploaded = true;
      }

      // 5. 완료 처리 (v2.0: folderId만 전달)
      await complete({ contentId: uploadData.id, folderId });

      onComplete?.({
        id: uploadData.id,
        preSignedUrl: uploadData.preSignedUrl,
        thumbnailPreSignedUrl: uploadData.thumbnailPreSignedUrl,
        thumbnailUploaded,
      });
      return { success: true, message: 'Video uploaded successfully.' };
    } catch (e) {
      throw new UploadError(
        e instanceof Error ? e.message : String(e),
        uploadData.id,
      );
    }
  };

  const audioUpload = async ({
    spaceId,
    folderId,
    file,
    project,
    onProgress,
    onComplete,
  }: BaseUploadParams) => {
    const fileId = generateRandomId();

    return performBaseUpload({
      file,
      spaceId,
      folderId,
      fileId,
      project,
      getMetadata: getAudioMetadata,
      createContent: (metadata) => ({
        type: 'AUDIO',
        duration: metadata.duration,
      }),
      onProgress,
      onComplete,
      contentType: 'Audio',
    });
  };

  const imageUpload = async ({
    spaceId,
    folderId,
    file,
    project,
    onProgress,
    onComplete,
  }: BaseUploadParams) => {
    const fileId = generateRandomId();

    return performBaseUpload({
      file,
      spaceId,
      folderId,
      fileId,
      project,
      getMetadata: getImageMetadata,
      createContent: (metadata) => ({
        type: 'IMAGE',
        width: metadata.width,
        height: metadata.height,
      }),
      onProgress,
      onComplete,
      contentType: 'Image',
    });
  };

  const fontUpload = async ({
    spaceId,
    folderId,
    file,
    project,
    onProgress,
    onComplete,
  }: BaseUploadParams) => {
    const fileId = generateRandomId();

    return performBaseUpload({
      file,
      spaceId,
      folderId,
      fileId,
      project,
      getMetadata: getFontMetadata,
      createContent: () => ({
        type: 'FONT',
      }),
      onProgress,
      onComplete,
      contentType: 'Font',
    });
  };

  const documentUpload = async ({
    spaceId,
    folderId,
    file,
    project,
    onProgress,
    onComplete,
  }: BaseUploadParams) => {
    const fileId = generateRandomId();

    return performBaseUpload({
      file,
      spaceId,
      folderId,
      fileId,
      project,
      // 문서(pdf/pptx)는 파싱할 미디어 메타데이터가 없다 — 파일 기본 정보만.
      getMetadata: async (documentFile: File) => ({
        fileName: documentFile.name,
        fileExtension:
          documentFile.name.split('.').pop()?.toLowerCase() ?? 'pdf',
        fileSize: documentFile.size,
      }),
      createContent: () => ({
        type: 'DOCUMENT',
      }),
      onProgress,
      onComplete,
      contentType: 'Document',
    });
  };

  return {
    videoUpload,
    audioUpload,
    imageUpload,
    fontUpload,
    documentUpload,
  };
};
